import { Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton
} from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setCurrentStageName } from '../../reducers/rtfoSlice';
import { rtfoConversionFactors } from './rtfoConversionFactors';

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10
  },
  table: {
    overflow: 'auto',
    marginTop: 0,
    marginBottom: 0,
    border: 'none',
    fontSize: '12px'
  }
});

export const RTFOISIPDataTable = ({ darkMode, currentRTFO }) => {
  // STYLES
  const classes = useStyles();
  const dispatch = useDispatch();
  const units = currentRTFO?.settings?.units || 'metric';

  const rows = useMemo(() => {
    if (!currentRTFO?.stages?.length > 0) return [];
    let stages = [];

    currentRTFO?.stageData &&
      Object.keys(currentRTFO?.stageData)?.forEach(stageName => {
        const stage = currentRTFO?.stageData?.[stageName];
        const isipColdbore = stage?.metrics?.isip;
        const isipBH = stage?.metrics?.isipBH;
        const fracGradient = stage?.metrics?.fracGradient;

        stages.push({
          id: stageName,
          startTimestamp: stage?.stageStartTimestamp,
          stageName,
          wellName: stage?.wellName,
          wellUwi: stage?.wellUwi,
          stageNumber: stage?.stageNumber,
          isipColdbore: Number((isipColdbore * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2)) || 0,
          isipBH: Number((isipBH * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2)) || 0,
          fracGradient: Number((fracGradient * (units === 'field' ? rtfoConversionFactors.fracGradient / 1000 : 1))?.toFixed(2)) || 0,
          falloffISIP: Number((stage?.metrics?.falloffISIP * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2)) || 0
        });
      });

    stages.sort((a, b) => b.startTimestamp - a.startTimestamp);

    return stages;
  }, [currentRTFO?.stageData, currentRTFO?.stages?.length, units]);

  try {
    const columns = [
      {
        field: 'stageName',
        headerName: 'Stage Name',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'wellName',
        headerName: 'Well Name',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'wellUwi',
        headerName: 'Well UWI',
        width: 125,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'stageNumber',
        headerName: 'Stage Number',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'isipColdbore',
        headerName: `ISIP Surf. (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'isipBH',
        headerName: `ISIP BH (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'fracGradient',
        headerName: `"Frac Gradient" (${units === 'field' ? 'psi/ft' : 'kPa/m'})`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'falloffISIP',
        headerName: `Falloff ISIP (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: true
      }
    ];

    return (
      <Paper className={classes.root} elevation={0}>
        <DataGrid
          density="compact"
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          className={classes.table}
          components={{
            Toolbar: CustomToolbar
          }}
          sx={{
            '& .super-app-theme--Current': {
              fontWeight: 'bold',
              backgroundColor: 'rgba(7, 88, 183, 0.25)' // highlight the current row
            },
            '& .super-app-theme--NoData': {
              color: 'lightgrey'
            },
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: darkMode ? '#263238' : 'whitesmoke'
            }
          }}
          style={{ fontSize: '12px' }}
          getRowClassName={params => {
            return params?.row?.stageName === currentRTFO?.currentStageName
              ? `super-app-theme--Current`
              : !currentRTFO?.stageData?.[params?.row?.stageName]?.coldboreData
                ? 'super-app-theme--NoData'
                : '';
          }}
          onRowClick={params => !currentRTFO?.isLoadingData && dispatch(setCurrentStageName(params?.row?.stageName))}
          // onRowClick={params => (currentRTFO?.stageData?.[params?.row?.stageName]?.coldboreData && !currentRTFO?.isLoadingData && dispatch(setCurrentStageName(params?.row?.stageName)))}
        />
      </Paper>
    );
  } catch (error) {
    return <></>;
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarFilterButton style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarDensitySelector style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarExport style={{ color: darkMode && 'lightgrey' }} />
      </GridToolbarContainer>
    );
  };
};
